// Everything-tab feed merge: interleaves the conversations stream with the virtual
// pitch-rows stream (conversationless pitches on requests the media user owns).
//
// Both streams are keyset-paginated newest-first, so a row is only safe to display once
// we know nothing newer can still arrive from the OTHER stream. Each stream that still
// has more pages contributes a "frontier" (the timestamp of its oldest FETCHED row —
// for pitches that's the fetch cursor, not the oldest kept row, since a whole page can
// dedupe away). Rows older than the newest frontier are held back until the blocking
// stream advances.
//
// Dedupe: a pitch whose conversation is already loaded shows only the conversation row.
// last_sent_at >= sent_at always, so with newest-first pagination the conversation is
// loaded at-or-before the point its pitch row would surface — dropping against loaded
// origin_ids is safe at any pagination depth. (Pitches whose conversations are trashed
// never enter pitchRows at all — useVirtualPitchRows excludes them at fetch time.)

export const rowTimestamp = (row) => (row.feed_row_type === 'pitch' ? row.pitch.sent_at : row.last_sent_at);

export default function mergeFeedRows({
  conversations,
  conversationsHasMore,
  pitchRows,
  pitchFrontier, // oldest fetched pitch sent_at while more pages remain; null when exhausted
  // Row ids exempt from the frontier cut: the SELECTED row must stay visible even when it
  // is deeper than either stream has paginated (deep links / carried selections are
  // injected upstream at sorted position — same contract useConversations' preserve+inject
  // had before the merge existed).
  pinnedRowIds = [],
}) {
  // Frontier = the oldest FETCHED conversation. A pinned (preserve+injected) deep
  // selection sorts to the bottom without representing fetch progress — skipping it keeps
  // the cut honest, else unfetched-range pitches surface early and later jump around.
  let frontierIndex = conversations.length - 1;
  while (frontierIndex >= 0 && pinnedRowIds.includes(conversations[frontierIndex].conversation_id)) {
    frontierIndex -= 1;
  }
  const conversationFrontier = conversationsHasMore && frontierIndex >= 0
    ? conversations[frontierIndex].last_sent_at
    : null;

  let cut = null;
  if (conversationFrontier && pitchFrontier) {
    cut = conversationFrontier > pitchFrontier ? conversationFrontier : pitchFrontier;
  } else {
    cut = conversationFrontier || pitchFrontier;
  }

  const loadedOriginIds = new Set(
    conversations.filter((c) => c.origin_type === 'Interact::Pitch').map((c) => c.origin_id),
  );

  const wrappedPitches = pitchRows
    .filter((pitch) => !loadedOriginIds.has(pitch.id))
    .map((pitch) => ({
      feed_row_type: 'pitch',
      conversation_id: `pitch-${pitch.id}`,
      pitch,
    }));

  const all = [...conversations, ...wrappedPitches];
  const visible = cut
    ? all.filter((row) => rowTimestamp(row) >= cut || pinnedRowIds.includes(row.conversation_id))
    : all;

  visible.sort((a, b) => {
    const ta = rowTimestamp(a);
    const tb = rowTimestamp(b);
    if (ta !== tb) return ta < tb ? 1 : -1;
    return a.conversation_id < b.conversation_id ? 1 : -1;
  });

  const hiddenRows = all.length - visible.length;

  return {
    rows: visible,
    hasMore: conversationsHasMore || Boolean(pitchFrontier) || hiddenRows > 0,
    // Which stream(s) must advance to reveal more rows: the one(s) sitting AT the cut.
    // hasMore-with-no-frontier (empty or all-pinned list) still advances, else loadMore
    // would call neither stream and the sentinel spins forever.
    advanceConversations: conversationsHasMore && (!conversationFrontier || conversationFrontier === cut),
    advancePitches: Boolean(pitchFrontier) && pitchFrontier === cut,
  };
}
